import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Play, ImageIcon, X, Film } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Gallery() {
  const [tab, setTab] = useState("photos");
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/gallery/photos").then(({ data }) => setPhotos(data)).catch(() => {});
    api.get("/gallery/videos").then(({ data }) => setVideos(data)).catch(() => {});
  }, []);

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-3xl mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-2">Gallery</div>
        <h1 className="serif text-4xl md:text-5xl font-semibold text-[#000080] leading-tight">
          Moments that move us <em className="text-[#FF9933] not-italic">forward</em>.
        </h1>
        <p className="mt-4 text-lg text-[#4A4A4A]">A growing archive of fieldwork, events, and the beautiful faces behind every milestone.</p>
      </div>

      <div className="flex gap-2 mb-8">
        <button onClick={() => setTab("photos")} data-testid="gallery-tab-photos"
          className={`px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${tab === "photos" ? "bg-[#000080] text-white" : "bg-[#F3EFE7] text-[#4A4A4A]"}`}>
          <ImageIcon className="w-4 h-4" /> Photos ({photos.length})
        </button>
        <button onClick={() => setTab("videos")} data-testid="gallery-tab-videos"
          className={`px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${tab === "videos" ? "bg-[#000080] text-white" : "bg-[#F3EFE7] text-[#4A4A4A]"}`}>
          <Film className="w-4 h-4" /> Videos ({videos.length})
        </button>
      </div>

      {tab === "photos" && (
        <div>
          {photos.length === 0 ? (
            <EmptyState icon={ImageIcon} text="Photos coming soon. Our team is uploading captures from the latest field visits." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((p) => (
                <button key={p.id} onClick={() => setViewing(p)}
                  data-testid={`photo-${p.id}`}
                  className="group relative aspect-square rounded-2xl overflow-hidden bg-[#F3EFE7] card-hover">
                  <img src={`${BACKEND_URL}${p.public_url}`} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <div className="text-white text-sm font-semibold truncate">{p.title}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "videos" && (
        <div>
          {videos.length === 0 ? (
            <EmptyState icon={Film} text="Videos coming soon. Our documentary team is editing field footage." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.map((v) => (
                <button key={v.id} onClick={() => setPlaying(v)} data-testid={`video-${v.id}`} className="text-left card-hover bg-white rounded-2xl overflow-hidden border border-[#eae5d9]">
                  <div className="relative aspect-video bg-black">
                    {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#FF9933] flex items-center justify-center shadow-lg">
                        <Play className="w-6 h-6 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="serif text-lg font-semibold text-[#000080]">{v.title}</div>
                    {v.description && <div className="text-sm text-[#737373] mt-1 line-clamp-2">{v.description}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setViewing(null)} data-testid="photo-lightbox">
          <button onClick={() => setViewing(null)} className="absolute top-6 right-6 text-white"><X className="w-8 h-8" /></button>
          <img src={`${BACKEND_URL}${viewing.public_url}`} alt={viewing.title} className="max-w-full max-h-full object-contain rounded-xl" />
        </div>
      )}

      {playing && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setPlaying(null)} data-testid="video-player">
          <button onClick={() => setPlaying(null)} className="absolute top-6 right-6 text-white z-10"><X className="w-8 h-8" /></button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe className="w-full h-full rounded-2xl" src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1`} title={playing.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="bg-white border border-dashed border-[#eae5d9] rounded-3xl p-16 text-center">
      <Icon className="w-10 h-10 text-[#FF9933] mx-auto mb-4" strokeWidth={1.4} />
      <p className="text-[#737373]">{text}</p>
    </div>
  );
}
